import { useState, useMemo, useEffect } from 'react'
import { ChevronsUpDown, Check, Globe, Plus, Settings2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { toastError } from '@/lib/errores'
import { useAuthStore, type ContextOwner } from '@/stores/auth'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command'
import { getInitials, avatarClasses } from '@/lib/identity'
import { cn } from '@/lib/utils'
import { formatNivel } from '@/lib/roles'

interface Props {
  collapsed?: boolean
}

/**
 * Selector de organización activa premium.
 *
 * Patrón inspirado en TenantSelector de machette-tech, ampliado con:
 *  - Búsqueda fuzzy con cmdk.
 *  - Agrupación "Tu organización" / "Otras organizaciones" (CO=organización; cliente=app conector).
 *  - Modo "Vista general" mockup (F4 cableará bloqueo escritura).
 *  - Avatares con iniciales + color hash determinista.
 *  - Versión colapsada (solo avatar) cuando sidebar está plegada.
 *
 * §16 plan PWA premium.
 */
export function OrgSwitcher({ collapsed = false }: Props) {
  const contextOwners = useAuthStore((s) => s.contextOwners)
  const activeCoId = useAuthStore((s) => s.activeCoId)
  const selectCo = useAuthStore((s) => s.selectCo)
  const loadMyContextOwners = useAuthStore((s) => s.loadMyContextOwners)
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (contextOwners.length === 0) {
      void loadMyContextOwners()
    }
  }, [contextOwners.length, loadMyContextOwners])

  const activeCo: ContextOwner | undefined = contextOwners.find((co) => co.id === activeCoId)

  const { propias, clientes } = useMemo(() => {
    const propias: ContextOwner[] = []
    const clientes: ContextOwner[] = []
    for (const co of contextOwners) {
      if (co.es_propietario) propias.push(co)
      else clientes.push(co)
    }
    return { propias, clientes }
  }, [contextOwners])

  async function handleSelect(coId: string) {
    if (coId === activeCoId) {
      setOpen(false)
      return
    }
    setOpen(false)
    try {
      await selectCo(coId)
      const co = contextOwners.find((c) => c.id === coId)
      toast.success(`Operas como ${co?.nombre ?? '?'}`)
      navigate('/app', { replace: true })
    } catch (e: any) {
      toastError(e, 'No se pudo cambiar de organización')
    }
  }

  if (!activeCo) return null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={collapsed ? `${activeCo.nombre} · ${formatNivel(activeCo.nivel)}` : undefined}
          className={cn(
            'group flex items-center gap-2.5 rounded-xl bg-sidebar-accent/40 transition-colors hover:bg-sidebar-accent/70',
            collapsed ? 'h-10 w-10 justify-center p-0' : 'w-full px-2.5 py-2 text-left',
          )}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls="org-switcher-listbox"
          aria-label="Cambiar de organización"
        >
          <span
            className={cn(
              'flex shrink-0 items-center justify-center rounded-lg font-semibold',
              avatarClasses(activeCo.nombre),
              collapsed ? 'h-8 w-8 text-[11px]' : 'h-9 w-9 text-xs',
            )}
          >
            {getInitials(activeCo.nombre)}
          </span>
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-sidebar-foreground">
                  {activeCo.nombre}
                </span>
                <span className="block truncate text-[11px] font-medium text-muted-foreground">
                  {formatNivel(activeCo.nivel)}
                  {activeCo.es_propietario ? '' : ' · Miembro'}
                </span>
              </span>
              <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={collapsed ? 'right' : 'bottom'}
        align="start"
        sideOffset={collapsed ? 8 : 6}
        className="w-[320px] p-0"
      >
        <Command>
          <CommandInput placeholder="Buscar organización..." />
          <CommandList id="org-switcher-listbox">
            <CommandEmpty>Sin resultados.</CommandEmpty>

            {propias.length > 0 && (
              <CommandGroup heading="Tu organización">
                {propias.map((co) => (
                  <OrgItem key={co.id} co={co} active={co.id === activeCoId} onSelect={() => handleSelect(co.id)} />
                ))}
              </CommandGroup>
            )}

            {clientes.length > 0 && (
              <CommandGroup heading={`Otras organizaciones (${clientes.length})`}>
                {clientes.map((co) => (
                  <OrgItem key={co.id} co={co} active={co.id === activeCoId} onSelect={() => handleSelect(co.id)} />
                ))}
              </CommandGroup>
            )}

            <CommandSeparator />

            <CommandGroup>
              <CommandItem
                value="__add"
                onSelect={() => {
                  setOpen(false)
                  navigate('/app/nuevo-cliente')
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Dar de alta un cliente
              </CommandItem>
              <CommandItem
                value="__manage"
                onSelect={() => {
                  setOpen(false)
                  navigate('/app/gobierno')
                }}
                className="gap-2"
              >
                <Settings2 className="h-4 w-4" />
                Gestionar organizaciones
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

interface OrgItemProps {
  co: ContextOwner
  active: boolean
  onSelect: () => void
}

function OrgItem({ co, active, onSelect }: OrgItemProps) {
  return (
    <CommandItem value={`${co.nombre} ${co.id}`} onSelect={onSelect} className="group gap-2.5 py-2">
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[11px] font-semibold',
          avatarClasses(co.nombre),
        )}
      >
        {getInitials(co.nombre)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{co.nombre}</span>
        {/* DC-5d (defecto de CLASE, no solo SelectCoPage): dedupe Propietario·Propietario
            + contraste del subtexto sobre el cian del item resaltado, por regla. */}
        <span className="block truncate text-[11px] text-muted-foreground group-data-[selected=true]:text-accent-foreground/80">
          {formatNivel(co.nivel)}
          {co.es_propietario ? '' : ' · Miembro'}
        </span>
      </span>
      {active && <Check className="h-4 w-4 shrink-0 text-primary" />}
    </CommandItem>
  )
}

/** Placeholder para el modo "Vista general" (F4 lo cableará a x-tenant-id:*) */
export function VistaGeneralIndicador() {
  return (
    <button
      type="button"
      disabled
      className="flex w-full items-center gap-2.5 rounded-xl border border-dashed border-border bg-sidebar-accent/20 px-2.5 py-2 text-left opacity-70"
      title="Próximamente: vista agregada de todas las organizaciones"
    >
      <Globe className="h-4 w-4 text-muted-foreground" />
      <span className="text-xs font-medium text-muted-foreground">Vista general (próximamente)</span>
    </button>
  )
}
